import React, { useCallback, useState } from 'react';
import FileViewerModal from '../../components/FileViewerModal';
import { fetchBlobViaProxy } from '../../utils/api';
import { detectFileViewerType, getFileNameFromPath } from '../../utils/fileViewer';

/**
 * Source-file viewer for the native report pages — the native equivalent of
 * Power BI's webURL hyperlinks on key numbers (hotelRevenue
 * [hotelRevenueAuditUrl+Sas] / latestHotelOccupancy [url+SASColumn]) and the
 * Night Audit report links.
 *
 * Files are fetched through POST /api/pbiPdfProxy (SAS appended server-side,
 * same flow as the PBI embed's dataHyperlinkClicked handler in Report.js)
 * and rendered in the app's FileViewerModal.
 *
 * Usage:
 *   const { openSourceFile, isLoadingFile, viewer } = useSourceFileViewer();
 *   <button onClick={() => openSourceFile(blobUrl)}>…</button>
 *   …
 *   {viewer}
 */
const KIND_LABEL = { audit: 'Night audit', occupancy: 'Occupancy', projection: 'Forecast' };

/** 'YYYY-MM-DD' → local Date without timezone shifting (new Date(iso) is UTC). */
function parseDayLocal(iso) {
  const [y, m, d] = String(iso || '').split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function chipDayLabel(iso, withYear) {
  const d = parseDayLocal(iso);
  if (!d) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(withYear ? { year: '2-digit' } : {}) });
}

function groupDayLabel(iso) {
  const d = parseDayLocal(iso);
  if (!d) return iso;
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * "received Jul 20, 8:42 AM" in the viewer's local timezone — the time is what
 * separates two same-day imports of the same file. Date-only fallback for
 * callers that don't supply the full instant.
 */
function receivedLabel(f) {
  if (f.receivedAtIso) {
    const d = new Date(f.receivedAtIso);
    if (!Number.isNaN(d.getTime())) {
      return `received ${d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`;
    }
  }
  return f.receivedAt && f.receivedAt !== f.date ? `received ${f.receivedAt}` : null;
}

/**
 * Filterable source-file picker — large date ranges pull in hundreds of
 * imports. Dates are first-class: one-click chips for each effective date in
 * the list (with file counts), rows grouped under date headers. Name search
 * and a received-date range live behind "More filters".
 */
function SourceFilePicker({ picker, onClose, onOpenFile }) {
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('');
  const [selectedDates, setSelectedDates] = useState([]);
  const [moreOpen, setMoreOpen] = useState(false);
  const [recFrom, setRecFrom] = useState('');
  const [recTo, setRecTo] = useState('');

  const kinds = [...new Set(picker.files.map((f) => f.kind).filter(Boolean))];
  // Newest first — the file you want is almost always a recent audit date.
  const dateOptions = [...new Set(picker.files.map((f) => f.date).filter(Boolean))].sort().reverse();
  const multiYear = new Set(dateOptions.map((d) => String(d).slice(0, 4))).size > 1;
  const countByDate = picker.files.reduce((acc, f) => {
    if (f.date) acc[f.date] = (acc[f.date] || 0) + 1;
    return acc;
  }, {});

  const toggleDate = (d) =>
    setSelectedDates((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));

  const shown = picker.files.filter((f) => {
    const name = (f.label || getFileNameFromPath(f.path, '')).toLowerCase();
    if (q && !name.includes(q.toLowerCase())) return false;
    if (kind && f.kind !== kind) return false;
    if (selectedDates.length && !selectedDates.includes(f.date)) return false;
    if (recFrom && (!f.receivedAt || f.receivedAt < recFrom)) return false;
    if (recTo && (!f.receivedAt || f.receivedAt > recTo)) return false;
    return true;
  });

  // Group the visible rows under date headers, newest date first, undated last.
  const groupOrder = [];
  const groupFiles = new Map();
  shown.forEach((f) => {
    const key = f.date || '';
    if (!groupFiles.has(key)) {
      groupFiles.set(key, []);
      groupOrder.push(key);
    }
    groupFiles.get(key).push(f);
  });
  groupOrder.sort((a, b) => (a === '' ? 1 : b === '' ? -1 : b.localeCompare(a)));
  // Newest received first inside each date group — ISO strings sort lexically.
  groupFiles.forEach((arr) =>
    arr.sort((a, b) => String(b.receivedAtIso || b.receivedAt || '').localeCompare(String(a.receivedAtIso || a.receivedAt || '')))
  );

  const fileRow = (f, i) => (
    <button
      key={`${f.path}-${i}`}
      type="button"
      className="rn-modal-row rn-srcfile-row"
      onClick={() => onOpenFile(f)}
    >
      <div className="rn-modal-row-main">
        <div className="rn-modal-row-title">{f.label || getFileNameFromPath(f.path, 'Source file')}</div>
        <div className="rn-modal-row-sub">
          {[receivedLabel(f), KIND_LABEL[f.kind] || null]
            .filter(Boolean)
            .join(' · ') || 'Imported file'}
        </div>
      </div>
      <span className="rn-srcfile-open">Open ›</span>
    </button>
  );

  return (
    <div className="rn-modal-backdrop" onClick={onClose}>
      <div className="rn-modal" role="dialog" aria-label={picker.title} onClick={(e) => e.stopPropagation()}>
        <div className="rn-modal-head">
          <h2>{picker.title}</h2>
          <span className="rn-pill-note">{shown.length === picker.files.length ? `${picker.files.length} files` : `${shown.length} of ${picker.files.length}`}</span>
          <div className="rn-spacer" />
          <button type="button" className="rn-icon-btn" title="Close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="rn-modal-sub">{picker.subtitle}</div>
        {dateOptions.length > 1 && (
          <div className="rn-srcfile-dates" role="group" aria-label="Filter by effective date">
            <button
              type="button"
              className={`rn-srcfile-date-chip${selectedDates.length === 0 ? ' rn-on' : ''}`}
              onClick={() => setSelectedDates([])}
            >
              All dates
            </button>
            {dateOptions.map((d) => (
              <button
                key={d}
                type="button"
                className={`rn-srcfile-date-chip${selectedDates.includes(d) ? ' rn-on' : ''}`}
                aria-pressed={selectedDates.includes(d)}
                onClick={() => toggleDate(d)}
              >
                {chipDayLabel(d, multiYear)}
                {countByDate[d] > 1 && <span className="rn-srcfile-date-count">{countByDate[d]}</span>}
              </button>
            ))}
          </div>
        )}
        <div className="rn-srcfile-filters">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search file name…"
            aria-label="Search file name"
          />
          {kinds.length > 1 && (
            <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="File type">
              <option value="">All types</option>
              {kinds.map((k) => (
                <option key={k} value={k}>{KIND_LABEL[k] || k}</option>
              ))}
            </select>
          )}
          <button
            type="button"
            className={`rn-srcfile-more${moreOpen || recFrom || recTo ? ' rn-on' : ''}`}
            onClick={() => setMoreOpen((v) => !v)}
            aria-expanded={moreOpen}
          >
            More filters {moreOpen ? '▴' : '▾'}
          </button>
        </div>
        {moreOpen && (
          <div className="rn-srcfile-filters rn-srcfile-filters-more">
            <label>
              <span>Received between</span>
              <input type="date" value={recFrom} onChange={(e) => setRecFrom(e.target.value)} aria-label="Received date from" />
              <i>–</i>
              <input type="date" value={recTo} onChange={(e) => setRecTo(e.target.value)} aria-label="Received date to" />
            </label>
            {(recFrom || recTo) && (
              <button
                type="button"
                className="rn-srcfile-more"
                onClick={() => {
                  setRecFrom('');
                  setRecTo('');
                }}
              >
                Clear
              </button>
            )}
          </div>
        )}
        <div className="rn-modal-list">
          {shown.length === 0 && <div className="rn-empty" style={{ padding: 16 }}>No files match the filters.</div>}
          {groupOrder.map((key) => (
            <React.Fragment key={key || 'undated'}>
              <div className="rn-srcfile-group-head">
                {key ? groupDayLabel(key) : 'No effective date'}
                <span className="rn-srcfile-group-count">
                  {groupFiles.get(key).length} {groupFiles.get(key).length === 1 ? 'file' : 'files'}
                </span>
              </div>
              {groupFiles.get(key).map(fileRow)}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

export function useSourceFileViewer() {
  const [file, setFile] = useState(null); // { objectUrl, fileType, fileName }
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [picker, setPicker] = useState(null); // { title, subtitle, files: [{path,date,kind}] }

  const openSourceFile = useCallback(async (blobUrl, fallbackName = 'Source file') => {
    if (!blobUrl) return;
    setFileError(null);
    setIsLoadingFile(true);
    try {
      const objectUrl = await fetchBlobViaProxy(blobUrl);
      setFile({
        objectUrl,
        fileType: detectFileViewerType(blobUrl, 'pdf'),
        fileName: getFileNameFromPath(blobUrl, fallbackName),
      });
    } catch (err) {
      setFileError(err?.message || 'Unable to open the source file.');
    } finally {
      setIsLoadingFile(false);
    }
  }, []);

  const closeViewer = useCallback(() => {
    setFile((prev) => {
      if (prev?.objectUrl) URL.revokeObjectURL(prev.objectUrl);
      return null;
    });
  }, []);

  /**
   * Open a number's backing files. One file → straight to the viewer;
   * several (the date range pulled in multiple imports) → a picker modal
   * listing every file linked to the data, each opening the viewer.
   * files: [{ path, date?, kind? }] or plain path strings.
   */
  const openSourceFiles = useCallback(
    (fileList, opts = {}) => {
      const list = (fileList || [])
        .map((f) => (typeof f === 'string' ? { path: f } : f))
        .filter((f) => f && f.path);
      if (!list.length) return;
      if (list.length === 1) {
        openSourceFile(list[0].path, opts.fallbackName);
        return;
      }
      setPicker({
        title: opts.title || 'Source files',
        subtitle: opts.subtitle || 'This number aggregates data from multiple imported files.',
        files: list,
      });
    },
    [openSourceFile]
  );

  const viewer = (
    <>
      {picker ? (
        <SourceFilePicker
          picker={picker}
          onClose={() => setPicker(null)}
          onOpenFile={(f) => {
            setPicker(null);
            openSourceFile(f.path, f.label);
          }}
        />
      ) : null}
      {file ? (
        <FileViewerModal
          url={file.objectUrl}
          fileType={file.fileType}
          fileName={file.fileName}
          onClose={closeViewer}
        />
      ) : null}
      {isLoadingFile ? (
        <div className="rn-file-loading" role="status">
          <span className="rn-file-loading-spinner" />
          Opening source file…
        </div>
      ) : null}
      {fileError ? (
        <div className="rn-file-loading rn-file-loading-error" role="alert" onClick={() => setFileError(null)}>
          {fileError}
        </div>
      ) : null}
    </>
  );

  return { openSourceFile, openSourceFiles, isLoadingFile, fileError, viewer };
}

/**
 * Orange dotted-underline link cell for key numbers backed by a source file
 * (mirrors the mockups' "Actual Room Revenue links" treatment).
 */
export function SourceFileLink({ onOpen, children, title = 'Open the source night-audit file' }) {
  if (!onOpen) return children ?? null;
  return (
    <button
      type="button"
      className="rn-cell-link"
      title={title}
      onClick={(e) => {
        // opening a source file must not also fire the row's cross-filter
        e.stopPropagation();
        onOpen();
      }}
    >
      {children}
    </button>
  );
}

export default useSourceFileViewer;
